import React, { createContext, useContext, useState, useCallback } from 'react';

const AdminAuthContext = createContext(null);

// Use backend URL from env or default to relative path
const API_BASE_URL = process.env.REACT_APP_API_URL || '';

export const useAdminAuth = () => {
  const context = useContext(AdminAuthContext);
  if (!context) {
    throw new Error('useAdminAuth must be used within AdminAuthProvider');
  }
  return context;
};

export const AdminAuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    const stored = sessionStorage.getItem('admin_auth');
    return stored ? JSON.parse(stored) : null;
  });
  const [error, setError] = useState(null);

  const login = useCallback(async (username, password) => {
    setError(null);
    try {
      // Test credentials by calling admin dashboard endpoint
      const response = await fetch(`${API_BASE_URL}/api/admin/dashboard`, {
        headers: {
          'Authorization': 'Basic ' + btoa(`${username}:${password}`),
        },
      });

      if (response.ok) {
        const authData = { username, credentials: btoa(`${username}:${password}`) };
        sessionStorage.setItem('admin_auth', JSON.stringify(authData));
        setIsAuthenticated(authData);
        return true;
      } else {
        setError('Invalid credentials');
        return false;
      }
    } catch (err) {
      setError('Login failed');
      return false;
    }
  }, []);

  const logout = useCallback(() => {
    sessionStorage.removeItem('admin_auth');
    setIsAuthenticated(null);
  }, []);

  const getAuthHeader = useCallback(() => {
    if (!isAuthenticated?.credentials) return {};
    return {
      'Authorization': `Basic ${isAuthenticated.credentials}`,
    };
  }, [isAuthenticated]);

  const adminFetch = useCallback(async (url, options = {}) => {
    const headers = {
      ...options.headers,
      ...getAuthHeader(),
    };
    // Prepend API base URL if the URL is relative
    const fullUrl = url.startsWith('http') ? url : `${API_BASE_URL}${url}`;
    return fetch(fullUrl, { ...options, headers });
  }, [getAuthHeader]);

  return (
    <AdminAuthContext.Provider
      value={{
        isAuthenticated: !!isAuthenticated,
        username: isAuthenticated?.username,
        error,
        login,
        logout,
        adminFetch,
        getAuthHeader,
      }}
    >
      {children}
    </AdminAuthContext.Provider>
  );
};
